<template>
  <div v-if="deepBoxBoxInfoStore.boxInfo" @dragover.prevent="onDragOver">
    <CoreNavigationDrawer
      v-bind="$attrs"
      id="nav-drawer"
      ref="navDrawer"
      class="mx-auto"
      data-test-id="box-navigation-drawer"
      disable-route-watcher
      touchless
      :width="256"
    >
      <!-- TODO(VUE-3-MIGRATION): check the replacement for `expand` props because was removed-->
      <v-list v-if="deepBoxBoxInfoStore.boxInfo" density="compact" nav>
        <template v-if="!isBoxPrivate">
          <!-- DASHBOARD -->
          <CoreNavigationDrawerListItem
            data-test-id="box-navigation-dashboard"
            draggable="false"
            exact
            :title="t('navigation.dashboard')"
            :to="{
              name: 'organization',
              params: {
                organization: deepAdminUsersMeStore.selectedOrganizationId,
              },
            }"
          >
            <template #icon>
              <v-icon
                v-tooltip="{
                  text: t('navigation.dashboard'),
                  location: settingsStore.navDrawerMini ? 'right' : 'top',
                }"
                class="mx-0"
              >
                far fa-house
              </v-icon>
            </template>
          </CoreNavigationDrawerListItem>
          <!-- /DASHBOARD -->
          <v-divider class="my-2 mx-2" />
        </template>

        <!-- MENU ITEMS -->
        <!-- MENU ITEM: GENERIC (Only if Box is from type 'private' -->
        <BoxNavigationDrawerGenericsList
          v-if="isBoxPrivate"
          :box-id="boxId"
          :generic-folders="genericFolders"
          :type-id="typeId"
        />
        <!-- MENU ITEM: GENERIC -->

        <!-- MENU ITEM: INBOX -->
        <CoreNavigationDrawerListItem
          v-if="canListQueue(deepBoxBoxInfoStore.boxInfo)"
          :data-node-id="FILE_TREE.FILE_TREE_INBOX"
          data-test-id="box-navigation-inbox"
          draggable="false"
          exact
          icon="far fa-envelope"
          :title="t('box_details.navigation.inbox')"
          :to="{
            name: 'organization-types-type-boxes-box-inbox',
            params: {
              type: typeId,
              box: boxId,
            },
          }"
          @dragenter.prevent
          @dragleave.prevent="dragLeave($event, FILE_TREE.FILE_TREE_INBOX)"
          @dragover.prevent="dragOver($event, FILE_TREE.FILE_TREE_INBOX)"
          @drop="dropNode($event, FILE_TREE.FILE_TREE_INBOX)"
          @icon:dblclick="settingsStore.navDrawerMini = false"
        >
          <template #append>
            <v-badge
              v-if="inboxCount !== undefined"
              color="primary"
              :content="inboxCount"
              :inline="true"
            >
              <v-icon
                v-if="settingsStore.navDrawerMini"
                icon="far fa-envelope"
              ></v-icon>
            </v-badge>
          </template>
          <template v-if="settingsStore.navDrawerMini" #icon>
            <v-badge
              v-if="inboxCount !== undefined"
              color="primary"
              :content="inboxCount"
            >
              <v-icon icon="far fa-envelope"></v-icon>
            </v-badge>
          </template>
        </CoreNavigationDrawerListItem>
        <!-- /MENU ITEM: INBOX -->

        <!-- MENU ITEM: FILES -->
        <BoxNavigationVTreeView
          v-if="canListFilesRoot(deepBoxBoxInfoStore.boxInfo) && !isBoxPrivate"
          ref="boxTreeview"
          :box-id="boxId"
          :type-id="typeId"
          @item:dragend="dragDrop.onDragEnd"
          @item:dragleave="dragLeave($event.event, $event.item)"
          @item:dragover="dragOver($event.event, $event.item)"
          @item:dragstart="onTreeItemDragStart($event.event, $event.item)"
          @item:drop="dropNode($event.event, $event.item)"
        />
        <!-- /MENU ITEM: FILES -->
        <!-- MENU ITEM: TRASH -->
        <CoreNavigationDrawerListItem
          v-if="canAccessTrash(deepBoxBoxInfoStore.boxInfo)"
          :data-node-id="FILE_TREE.FILE_TREE_TRASH"
          data-test-id="box-navigation-trash"
          draggable="false"
          exact
          icon="far fa-trash"
          :title="t('box_details.navigation.trash')"
          :to="{
            name: 'organization-types-type-boxes-box-trash',
            params: {
              type: typeId,
              box: boxId,
            },
          }"
          @click="treeViewActive = []"
          @dragenter.prevent
          @dragleave="dragLeave($event, FILE_TREE.FILE_TREE_TRASH)"
          @dragover="dragOver($event, FILE_TREE.FILE_TREE_TRASH)"
          @drop="dropNode($event, FILE_TREE.FILE_TREE_TRASH)"
          @icon:dblclick="settingsStore.navDrawerMini = false"
        />
        <!-- /MENU ITEM: TRASH -->
        <!-- MENU ITEM: ARCHIVE -->
        <template v-if="archiveFolder">
          <v-divider class="my-2 mx-2" />
          <CoreNavigationDrawerListItem
            data-test-id="box-navigation-archive"
            exact
            icon="far fa-cabinet-filing"
            :title="archiveFolder.displayName"
            :to="{
              name: 'organization-types-type-boxes-box-files-node',
              params: {
                type: typeId,
                box: boxId,
                node: archiveFolder.nodeId,
              },
            }"
            @icon:dblclick="settingsStore.navDrawerMini = false"
          />
          <v-divider class="my-2 mx-2" />
        </template>
        <!-- /MENU ITEM: ARCHIVE -->
        <template v-if="deepBoxBoxInfoStore.isOwnedBox">
          <v-divider
            v-if="
              !archiveFolder &&
              (canSeeDeadlines ||
                (deepBoxBoxInfoStore.boxInfo &&
                  deepBoxBoxInfoStore.boxInfo.relations.length > 0))
            "
            class="my-2 mx-2"
          />
          <!-- MENU ITEM: DEADLINES -->
          <CoreNavigationDrawerListItem
            v-if="canSeeDeadlines"
            data-test-id="box-navigation-deadlines"
            exact
            icon="far fa-calendar-clock"
            :title="t('box_details.navigation.deadlines')"
            :to="{
              name: 'organization-types-type-boxes-box-search',
              params: {
                type: typeId,
                box: boxId,
              },
              query: deadlinesSearchQuery,
            }"
            @icon:dblclick="settingsStore.navDrawerMini = false"
          />
          <!-- /MENU ITEM: DEADLINES -->
        </template>

        <!-- MENU ITEM: RELATIONS -->
        <div
          v-if="
            deepBoxBoxInfoStore.boxInfo &&
            deepBoxBoxInfoStore.boxInfo.relations.length > 0
          "
        >
          <v-divider v-if="!isArchiveLastBeforeRelations" class="my-2 mx-2" />
          <CoreNavigationDrawerListItem
            v-for="boxRelation in deepBoxBoxInfoStore.boxInfo.relations"
            :key="boxRelation.boxNodeId"
            :data-test-id="`box-navigation-relation-${boxRelation.boxName}`"
            exact
            :subtitle="boxRelation.deepBoxName"
            :title="boxRelation.boxName"
            :to="{
              name: 'organization-types-type-boxes-box-files',
              params: {
                type: boxRelation.deepBoxNodeId,
                box: boxRelation.boxNodeId,
              },
            }"
          >
            <template #icon>
              <v-icon>
                <BoxAvatar
                  v-tooltip="{
                    text: boxRelation.boxName,
                    location: settingsStore.navDrawerMini ? 'right' : 'top',
                  }"
                  :box="boxRelation as BoxEntry"
                  :box-size="20"
                />
              </v-icon>
            </template>
          </CoreNavigationDrawerListItem>
        </div>
        <!-- /MENU ITEM: RELATIONS -->
        <!-- /MENU ITEMS -->
      </v-list>

      <template #append>
        <v-list density="compact" nav>
          <CoreNavigationDrawerListItem
            v-if="settingsDevStore.canModeFeature('DEEP_FLOW') && !xs"
            data-test-id="box-navigation-deepflow-settings"
            exact
            :href="deepFlowHref"
            icon="far fa-diagram-project"
            target="_blank"
            title="DeepFlow"
          >
            <template v-if="!settingsStore.navDrawerMini" #append>
              <DeepStageLabel display-as="chip" flag="DEV" size="x-small" />
            </template>
          </CoreNavigationDrawerListItem>
          <CoreNavigationDrawerListItem
            v-if="canAccessDeepOSettings"
            data-test-id="box-navigation-deepo-settings"
            exact
            :href="deepoSettingsHref"
            icon="far fa-gear"
            target="_blank"
            :title="t('navigation.deepo_settings')"
            @icon:dblclick="settingsStore.navDrawerMini = false"
          />
          <CoreNavigationDrawerListItem
            v-if="userCanAdminAccess && adminOrAccessOrgId"
            data-test-id="box-navigation-deepbox-settings"
            exact
            :href="
              getDeepAdminOrgDeepBoxTypeBoxUrl(
                adminOrAccessOrgId,
                typeId,
                boxId,
              )
            "
            icon="far fa-gear"
            target="_self"
            :title="t('navigation.deepbox_settings')"
            @icon:dblclick="settingsStore.navDrawerMini = false"
          />
        </v-list>
      </template>
    </CoreNavigationDrawer>
  </div>
</template>

<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import { FILE_TREE } from '@/constants'
import { SECTIONS } from '@/types/sections'
import { BoxEntry } from '@deepcloud/api-deep-box'
import { DeepStageLabel } from '@deepcloud/deep-ui-lib'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import { useDeepAdminUsersMeStore } from '@/stores/deepadmin/users/users-me'
import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core'
import { useDeepBoxDeepBoxesBoxesFilesTreeStore } from '@/stores/deepbox/deepboxes/boxes/files-tree'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useSettingsStore } from '@/stores/settings/settings'
import { useSettingsDevStore } from '@/stores/settings/settings-dev'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useAppsUrls } from '@/composables/apps-urls'
import { useBox } from '@/composables/box/box'
import { useBoxAccessPolicy } from '@/composables/box/box-access-policy'
import { useBoxAdmin } from '@/composables/box/box-admin'
import { useBoxArchive } from '@/composables/box/box-archive'
import { useBoxNavigationDrawerDragDrop } from '@/composables/box/box-navigation-drawer-drag-drop'
import { useContextGenericFolders } from '@/composables/context/ctx-generics'
import BoxAvatar from '@/components/box/BoxAvatar.vue'
import BoxNavigationDrawerGenericsList from '@/components/box/BoxNavigationDrawerGenericsList.vue'
import BoxNavigationVTreeView from '@/components/box/BoxNavigationVTreeView.vue'
import CoreNavigationDrawer from '@/components/core/CoreNavigationDrawer.vue'
import CoreNavigationDrawerListItem from '@/components/core/CoreNavigationDrawerListItem.vue'
import { getSearchByTagRouteQuery } from '@/components/search/search-by-tag.ts'
import type { FileTree } from '@/types/file-tree.ts'

const props = defineProps({
  typeId: {
    type: String,
    default: null,
  },
  boxId: {
    type: String,
    default: null,
  },
})

const { mdAndUp, xs } = useDisplay()
const device = inject(DeviceKey)
const { t } = useI18n()

const deepBoxAdminStore = useDeepBoxAdminStore()
const deepAdminUsersMeStore = useDeepAdminUsersMeStore()
const deepBoxCoreStore = useDeepBoxCoreStore()
const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
const deepBoxDeepBoxesBoxesFilesTreeStore =
  useDeepBoxDeepBoxesBoxesFilesTreeStore()
const settingsStore = useSettingsStore()
const settingsDevStore = useSettingsDevStore()
const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()

const treeViewActive = ref([])

const deadlinesSearchQuery = computed(() =>
  getSearchByTagRouteQuery('deadline', props.boxId),
)

const { isBoxPrivate } = useBox(computed(() => deepBoxCoreStore.selectedBox))

const genericFolders = useContextGenericFolders()

const { archiveFolder, lockNodeRemoval } = useBoxArchive()

function isInArchiveTree(item: FileTree) {
  if (!archiveFolder.value) return false

  const { filesTree, getFileTreeNodeByNodeId } =
    deepBoxDeepBoxesBoxesFilesTreeStore
  const archiveTree = getFileTreeNodeByNodeId(
    archiveFolder.value.nodeId,
    filesTree,
  )
  return (
    !!archiveTree &&
    (archiveTree.nodeId === item.nodeId ||
      !!getFileTreeNodeByNodeId(item.nodeId, archiveTree.children))
  )
}

function onTreeItemDragStart(e: DragEvent, item: FileTree) {
  if (isInArchiveTree(item)) lockNodeRemoval(item)
  dragStart(e, item)
}

const { adminBox } = storeToRefs(deepBoxAdminStore)
const { adminOrAccessOrgId, userCanAdminAccess } = useBoxAdmin(adminBox)

const deepoSettingsHref = computed(() => {
  return `${import.meta.env.VITE_DEEPBOX_EDITOR_BASE_URL}/${
    adminOrAccessOrgId.value
  }/masterdata/types/${props.typeId}/boxes/${props.boxId}`
})

const deepFlowHref = computed(() => {
  const url = new URL(import.meta.env.VITE_DEEPFLOW_FRONTEND_BASE_URL)

  if (adminOrAccessOrgId.value) {
    url.searchParams.append('orgId', adminOrAccessOrgId.value)
  }
  url.searchParams.append('typeId', props.typeId)
  url.searchParams.append('boxId', props.boxId)
  return url.toString()
})

const { getDeepAdminOrgDeepBoxTypeBoxUrl } = useAppsUrls()

const { canListFilesRoot, canAccessTrash, canListQueue } = useBoxAccessPolicy()

const inboxCount = computed(() => deepBoxBoxInfoStore.boxInfo?.queueCount)
const canSeeDeadlines = computed(() =>
  deepBoxBoxInfoStore.boxInfo?.supportedFeatureTags?.includes('deadline'),
)

const isArchiveLastBeforeRelations = computed(
  () =>
    !!archiveFolder.value &&
    !(deepBoxBoxInfoStore.isOwnedBox && canSeeDeadlines.value),
)

const canAccessDeepOSettings = computed(
  () =>
    deepBoxBoxInfoStore.boxInfo &&
    deepBoxBoxInfoStore.boxInfo.boxVariant &&
    ['advanced', 'advanced-per-use'].includes(
      deepBoxBoxInfoStore.boxInfo.boxVariant,
    ) &&
    userCanAdminAccess.value &&
    !xs.value,
)

watch(
  () => settingsStore.navDrawerMini,
  (newValue) => {
    if (newValue) {
      deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened = false
    }
  },
)

watch(
  () => mdAndUp.value,
  (newValue) => {
    if (!newValue && settingsStore.navDrawerMini) {
      settingsStore.navDrawerMini = false
    }
  },
)

watch(
  () => device?.isDesktop,
  (newValue) => {
    if (newValue && settingsStore.navDrawerMini) {
      settingsStore.navDrawerMini = false
    }
  },
)

// `canShow` alone decides visibility; `nodeId` may be undefined if
// `boxInfo.roots` doesn't have it yet.
function asRootFileTree(
  canShow: boolean,
  nodeId: string | undefined,
  name: string,
  mimeType: string,
  canAddChildren: boolean,
): FileTree | undefined {
  if (!canShow) return undefined

  return {
    nodeId,
    name,
    displayName: name,
    mimeType,
    type: 'folder',
    policy: { canAddChildren },
    children: [],
    parentNodeId: '',
  } as unknown as FileTree
}

function getNodeRoots() {
  const boxInfo = deepBoxBoxInfoStore.boxInfo
  const roots = boxInfo?.roots
  if (!boxInfo || !roots) return

  const { boxPolicy } = boxInfo

  deepBoxDeepBoxesBoxesNodesStore.rootNodes.queue = asRootFileTree(
    canListQueue(boxInfo),
    roots.queue,
    SECTIONS.INBOX,
    MIME_TYPES.MIME_TYPE_INBOX,
    boxPolicy?.canAddQueue ?? false,
  )

  const filesRoot = asRootFileTree(
    canListFilesRoot(boxInfo),
    roots.files,
    SECTIONS.FILES,
    MIME_TYPES.MIME_TYPE_FILES,
    boxPolicy?.canAddFilesRoot ?? false,
  )
  deepBoxDeepBoxesBoxesNodesStore.rootNodes.files = filesRoot
  if (filesRoot) {
    const { filesTree } = deepBoxDeepBoxesBoxesFilesTreeStore
    if (filesTree.length === 0) {
      filesTree.push({ ...filesRoot, children: [] })
    } else if (filesTree[0].nodeId !== filesRoot.nodeId) {
      filesTree.splice(0, 1, { ...filesRoot, children: [] })
    }
  }

  deepBoxDeepBoxesBoxesNodesStore.rootNodes.trash = asRootFileTree(
    canAccessTrash(boxInfo),
    roots.trash,
    SECTIONS.TRASH,
    MIME_TYPES.MIME_TYPE_TRASH,
    boxPolicy?.canAccessTrash ?? false,
  )
}

// Resync whenever `boxInfo.roots` changes (new box, or a refetch) so we
// never keep serving a previous box's stale roots.
watch(
  () => deepBoxBoxInfoStore.boxInfo?.roots,
  (roots) => {
    if (roots) getNodeRoots()
  },
)

// `rootNodes` can also get cleared externally (e.g. BoxLayout.vue's
// `$reset()`) without `boxInfo.roots` changing, so repopulate if it's empty.
watch(
  () => {
    const roots = deepBoxBoxInfoStore.boxInfo?.roots
    if (!roots) return false
    const { queue, files, trash } = deepBoxDeepBoxesBoxesNodesStore.rootNodes
    return !queue && !files && !trash
  },
  (needsNodeRoots) => {
    if (needsNodeRoots) getNodeRoots()
  },
  { immediate: true },
)

const boxTreeviewRef = useTemplateRef('boxTreeview')

const { dragDrop, dragOver, dragLeave, dragStart, dropNode, onDragOver } =
  useBoxNavigationDrawerDragDrop({
    typeId: () => props.typeId,
    boxId: () => props.boxId,
    onExpandNode: (item) => boxTreeviewRef.value?.openItem(item),
  })
</script>
